import React, { useEffect, useRef } from "react";
import ContentEditable, { ContentEditableEvent } from "react-contenteditable";
import cx from "classnames";
import { QuestionAnswer, ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";
import linkifyHelper from "../../helpers/linkifyHelper";
import DOMPurify, { type Config } from "dompurify";

interface SmartTextAreaProps {
  /** The onChange event, for handling state changes */
  onChange(newValue: string): void;
  /** An event to call when the input loses focus */
  onBlur?(
    latestValue: string,
    nonStateValue?: QuestionAnswer | null
  ): void | undefined;
  /** The current value for the textarea */
  value: string | undefined;
  /** The Id attribute for the input element, to match the Label */
  inputId?: string;
  /** The css class names to apply */
  className?: string;
  /** Whether or not the editable area should grow in height */
  autoGrowHeight?: boolean | undefined;
  /** The Placeholder attribute value for the form element */
  placeholder?: string | undefined;
  /** A ref for calling methods on the textarea element, e.g. to focus it */
  inputRef?: React.RefObject<HTMLTextAreaElement> | undefined;
  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  isReadOnly?: boolean;
  maxLength?: number | undefined;
}

/**
 * A rich text area component that provides:
 * - Auto-growing height to avoid excessive scrolling
 * - Auto-linkification of URLs as the user types
 * - Advanced paste handling with MS Word HTML cleanup
 * - Content sanitization to prevent XSS and corrupted HTML
 * - Validation error display
 *
 * Architecture:
 * - Uses ContentEditable for rich text editing
 * - Maintains 3 state values: raw (internal HTML), display (linkified HTML), valueToSave (plain text with URLs)
 * - All incoming content is sanitized via fixCorruptedContent() to handle legacy data issues
 * - Paste events are intercepted to clean Word HTML before insertion
 */
const SmartTextArea = ({
  onChange,
  onBlur = undefined,
  showValidationErrors = false,
  validationResult = null,
  value = "",
  inputId = "",
  className = "rounded-md border-0 border-none",
  autoGrowHeight = true,
  placeholder = "",
  inputRef = undefined,
  isReadOnly = false,
  maxLength = undefined,
}: SmartTextAreaProps) => {
  const stateValue = useRef({
    display: "",
    raw: "",
    valueToSave: "",
  });

  const contentEditableRef = useRef<HTMLElement>(null);

  /** DOMPurify config: strict allow-list */
  const purifierConfig: Config = {
    ALLOWED_TAGS: [
      "b",
      "i",
      "u",
      "em",
      "strong",
      "ul",
      "ol",
      "li",
      "p",
      "br",
      "span",
      "a",
    ],
    ALLOWED_ATTR: ["href"],
    FORBID_TAGS: ["style", "img", "script"],
    FORBID_ATTR: ["style", "class"],
    KEEP_CONTENT: true, // Keep text content even if tags are removed
  };

  /**
   * Removes MS Word-specific HTML pollution when content is pasted from Word.
   * Word exports HTML with extensive metadata, proprietary tags, and formatting
   * that can break contentEditable behavior and create visual inconsistencies.
   *
   * This function strips:
   * - Namespaced XML tags (e.g., <o:p>, <w:r>)
   * - Conditional comments and IE-specific markup
   * - MSO-specific classes and attributes
   * - Document structure tags (html, head, body, meta)
   * - Inline styles and excessive whitespace
   *
   * Called only when Word HTML is detected in paste events.
   */
  const cleanWordHTML = (html: string): string => {
    return html
      .replace(/<\s*\/?\s*\w+:[^>]*>/gi, "") // Namespaced tags (e.g., <o:p>, <w:r>)
      .replace(/<!--\[if [^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, "") // Conditional comments
      .replace(/<!--[\s\S]*?-->/g, "") // Regular comments
      .replace(/\sstyle=("|\')(.*?)\1/gi, "") // Inline styles
      .replace(/\sclass=("|\')(mso[^"']*|Mso[^"']*)\1/gi, "") // MSO-specific classes
      .replace(/\s(class|id)=("|\')(.*?)\2/gi, "") // Remaining class/id attrs
      .replace(/<meta[^>]*>/gi, "") // Meta tags
      .replace(/<link[^>]*>/gi, "") // Link tags
      .replace(/<title[^>]*>.*?<\/title>/gi, "") // Title tags
      .replace(/<head[^>]*>.*?<\/head>/gi, "") // Head sections
      .replace(/<body[^>]*>/gi, "") // Body opening tags
      .replace(/<\/body>/gi, "") // Body closing tags
      .replace(/<html[^>]*>/gi, "") // HTML opening tags
      .replace(/<\/html>/gi, "") // HTML closing tags
      .replace(/<\?xml[^>]*>/gi, "") // XML declarations
      .replace(/<!DOCTYPE[^>]*>/gi, "") // DOCTYPE declarations
      .replace(/&nbsp;/gi, " ") // Convert non-breaking spaces to regular spaces
      .replace(/[ \t]+/g, " ") // Collapse horizontal whitespace but preserve newlines
      .replace(/\n\s*\n/g, "\n") // Collapse multiple blank lines to single
      .trim();
  };

  /**
   * Fixes corrupted HTML that may exist in legacy data or from previous paste operations.
   *
   * Problem: Earlier versions of this component could save HTML with contenteditable="false"
   * attributes on <span> tags (instead of only <a> tags), making those sections uneditable.
   * This occurred when Word HTML had malformed style attributes that got partially parsed,
   * creating fragments like "https://size:11.0pt" which were then treated as URLs.
   *
   * Solution: Strip these problematic attributes and malformed URL fragments from all content.
   * This runs on all incoming data (paste, load, updates) to ensure editability.
   *
   * @param html - The HTML string to sanitize
   * @returns Clean HTML without contenteditable="false" on spans or malformed attributes
   */
  const fixCorruptedContent = (html: string): string => {
    return (
      html
        // Remove contenteditable and target attributes from span tags
        .replace(
          /<span([^>]*)contenteditable\s*=\s*["'][^"']*["']([^>]*)>/gi,
          "<span$1$2>"
        )
        .replace(
          /<span([^>]*)target\s*=\s*["'][^"']*["']([^>]*)>/gi,
          "<span$1$2>"
        )
        // Remove any malformed https:// fragments that aren't proper hrefs
        .replace(
          /https:\/\/[^"\s>]*(?:size|font|family|language|mso-)[^"\s>]*/gi,
          ""
        )
        // Clean up any remaining empty or malformed attributes
        .replace(/\s+>/g, ">")
        .replace(/<span\s+>/g, "<span>")
    );
  };

  /**
   * Inserts HTML at the current cursor position in the contentEditable element.
   * Uses the Selection API to properly handle cursor position and content insertion.
   *
   * Note: We use Selection API (not deprecated execCommand) for better future compatibility.
   * The fragment approach ensures proper DOM structure and cursor positioning.
   *
   * @param html - The HTML string to insert at the cursor position
   */
  const insertHTMLAtCaret = (html: string) => {
    if (!contentEditableRef.current) return;

    // Focus the contentEditable element
    contentEditableRef.current.focus();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // If no selection, append to the end
      contentEditableRef.current.innerHTML += html;
      return;
    }

    const range = selection.getRangeAt(0);
    range.deleteContents();

    // Create a document fragment from the HTML
    const fragment = range.createContextualFragment(html);

    // Insert the fragment
    range.insertNode(fragment);

    // Move cursor to end of inserted content
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  useEffect(() => {
    // Initialize component state when a value is first provided
    // This typically happens when loading data from the database
    if (value !== "" && stateValue.current.raw === "") {
      // Check if the value contains known corruption patterns before running expensive cleanup
      const needsFixing =
        /contenteditable\s*=\s*["']false["']|https:\/\/[^"\s>]*(?:size|font|family)/.test(
          value
        );
      const cleanedValue = needsFixing ? fixCorruptedContent(value) : value;

      updateStateValues(cleanedValue, false);
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = stateValue.current.display;
      }
    }
  }, [value]);

  /**
   * Handles paste events to clean and sanitize content before insertion.
   *
   * Flow:
   * 1. Intercept paste event to access clipboard data
   * 2. Detect if HTML is from MS Word (via Word-specific patterns)
   * 3. Clean Word HTML if detected, otherwise use raw HTML
   * 4. Run DOMPurify to sanitize against XSS
   * 5. Fallback to plain text if HTML becomes empty after cleaning
   * 6. Insert at cursor position and trigger change handler
   *
   * This prevents Word's bloated HTML from polluting the content and ensures
   * consistent formatting across different paste sources.
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    e.preventDefault();

    const clipboardData = e.clipboardData;
    const htmlData = clipboardData.getData("text/html");
    const plainText = clipboardData.getData("text/plain");

    let cleanHTML = "";

    if (htmlData) {
      // Detect Word HTML by checking for Word-specific patterns (namespaces, MSO classes, etc.)
      const needsWordCleaning =
        /<\w+:[^>]*>|class=["']?Mso|class=["']?mso|xmlns:|<o:|<w:|<!--\[if/i.test(
          htmlData
        );

      const wordCleaned = needsWordCleaning
        ? cleanWordHTML(htmlData)
        : htmlData;

      cleanHTML = String(DOMPurify.sanitize(wordCleaned, purifierConfig));

      // Normalize pasted HTML: merge bullets with text, collapse excessive spacing
      const normalizePastedContent = (html: string): string => {
        try {
          const container = document.createElement("div");
          container.innerHTML = html;

          // If proper list markup exists, leave it as-is
          if (container.querySelector("ul,ol")) {
            return html;
          }

          const bulletPattern = /^\s*[•·‣⁃▪▫‒–—―\-\*]\s*$/;
          const paragraphs = Array.from(container.querySelectorAll("p"));

          // Pass 1: Merge bullet-only paragraphs with following text
          for (let i = paragraphs.length - 1; i >= 0; i--) {
            const p = paragraphs[i];
            const text = (p.textContent || "").trim();

            // If this is just a bullet and next paragraph has text, merge them
            if (bulletPattern.test(text) && i < paragraphs.length - 1) {
              const next = paragraphs[i + 1];
              if ((next.textContent || "").trim().length > 0) {
                p.innerHTML = text + " ";
                while (next.firstChild) {
                  p.appendChild(next.firstChild);
                }
                next.remove();
              }
            }
            // Remove empty paragraphs
            else if (text.length === 0) {
              p.remove();
            }
          }

          return container.innerHTML;
        } catch {
          return html;
        }
      };

      cleanHTML = normalizePastedContent(cleanHTML);

      // If HTML becomes empty after cleaning, fall back to plain text
      if (
        !cleanHTML ||
        cleanHTML.trim() === "" ||
        cleanHTML === "<span></span>"
      ) {
        cleanHTML = `<p>${DOMPurify.sanitize(plainText || "")}</p>`;
      }
    } else if (plainText) {
      cleanHTML = `<p>${DOMPurify.sanitize(plainText)}</p>`;
    } else {
      return;
    }

    // Insert the cleaned HTML at the current caret position
    insertHTMLAtCaret(cleanHTML);

    // Use requestAnimationFrame for more reliable timing than setTimeout
    requestAnimationFrame(() => {
      if (contentEditableRef.current) {
        const newHTML = contentEditableRef.current.innerHTML;

        // Create a synthetic event to trigger the change handler
        const syntheticEvent = {
          target: { value: newHTML },
        } as ContentEditableEvent;

        handleChange(syntheticEvent);
      }
    });
  };

  /**
   * Handles content changes in the contentEditable element.
   * Ignores selection events to avoid unnecessary processing during text highlighting.
   */
  const handleChange = (e: ContentEditableEvent) => {
    // Skip processing if the user is just selecting text (not editing)
    // This prevents unnecessary state updates and improves performance
    if (userIsPerformingTextSelection()) {
      return;
    }

    updateStateValues(e.target.value, true);
    onChange(stateValue.current.valueToSave);
  };

  const userIsPerformingTextSelection = (): boolean => {
    const contentDiv = contentEditableRef.current;

    if (!contentDiv) {
      return false;
    }

    // Get the current selection
    const selection = window.getSelection();

    if (!selection) {
      return false;
    }

    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    // If there's any selection, return true
    return range !== null && selection.toString().length > 0;
  };

  const handleBlur = () => {
    // First convert any nbsp; to spaces (we only do this on blur to avoid issues with the cursor position)
    const rawTextValue = linkifyHelper.replaceNbspWithSpace(
      stateValue.current.raw
    );

    // To handle links being pasted in at the end of the content, without needing a whitespace after to trigger the linkify,
    // we run the same function on blur to ensure all links are clickable
    updateStateValues(rawTextValue, false);

    // If the display HTML has changed, update the contentEditable div
    // (for some reason, just setting the value of `stateValue.current.display` doesn't update the contentEditable div on blur)
    if (
      contentEditableRef.current &&
      contentEditableRef.current.innerHTML !== stateValue.current.display
    ) {
      contentEditableRef.current.innerHTML = stateValue.current.display;
    }

    if (onBlur) {
      onBlur(stateValue.current.valueToSave);
    }
  };

  /**
   * Updates the internal state with three representations of the content:
   * 1. raw: The sanitized HTML from contentEditable
   * 2. display: HTML with clickable links (via linkifyHelper)
   * 3. valueToSave: Plain text representation with raw URLs (for database storage)
   *
   * All incoming content is sanitized to remove corruption before processing.
   *
   * @param rawValue - The raw HTML from the contentEditable element
   * @param requireWhitespace - Whether URLs need trailing whitespace to be linkified
   */
  const updateStateValues = (rawValue: string, requireWhitespace: boolean) => {
    // Sanitize to remove any contenteditable="false" on spans or malformed attributes
    const sanitized = fixCorruptedContent(rawValue);

    stateValue.current.raw = sanitized;
    stateValue.current.display = linkifyHelper.convertRawValueForHtmlDisplay(
      sanitized,
      requireWhitespace
    );
    stateValue.current.valueToSave =
      linkifyHelper.convertRawValueForSaving(sanitized);
  };

  return (
    <div>
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}
      <div className={cx("hidden print:block print-avoid-break", className)}>
        {value ? value : ""}
      </div>
      <ContentEditable
        id={inputId}
        innerRef={contentEditableRef}
        html={stateValue.current.display} // innerHTML of the editable div
        disabled={isReadOnly} // use true to disable editing
        onChange={handleChange} // handle innerHTML change
        onBlur={handleBlur}
        onPaste={handlePaste}
        className={cx(
          "p-2 focus:outline-none focus:ring-0 disabled:text-gray-600 min-h-16 print:hidden",
          className,
          isReadOnly ? "cursor-not-allowed" : "",
          autoGrowHeight ? "max-h-32 overflow-y-auto" : ""
        )}
        placeholder={placeholder}
        tagName="div"
      />
    </div>
  );
};

export default SmartTextArea;
